import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import {
    EditTask,
    Task,
    TaskConfigField,
    TaskContentResponses,
    TaskLabel,
    TaskSwimLane,
} from '@app/interfaces/lm/kanban-board';
import { Account } from '@app/interfaces/sys/account';
import { SelectOption } from '@app/interfaces/sys/dict';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { ConfirmationService } from '@services/sys/confirmation.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FieldItemComponent } from './components/field-item/field-item.component';

@Component({
    selector: 'app-task-fields',
    templateUrl: './task-fields.component.html',
    styleUrl: './task-fields.component.less',
})
export class TaskFieldsComponent implements OnInit, OnChanges {
    @Input() taskInfo!: Task;
    @Input() swimLaneList: TaskSwimLane[] = [];
    @Input() accountList: Account[] = [];
    @Input() projectMembers: SelectOption[] = [];
    @Input() defaultFields: TaskConfigField[] = [];
    @Input() moreFields: TaskConfigField[] = [];
    @Input() templateAllFields: TaskConfigField[] = [];
    @Input() inactiveUsers: boolean = false;
    @Output() saveField = new EventEmitter();
    @Output() taskInfoChange = new EventEmitter<Task>();
    @ViewChildren(FieldItemComponent) fieldItems!: QueryList<FieldItemComponent>;
    statusLoading = false;
    templateFields: TaskConfigField[] = [];
    configFieldsForm: {
        [fieldName: string]: TaskContentResponses
    } = {};
    templateFieldObj: {
        [fieldName: string]: TaskConfigField
    } = {};
    lastTaskInfo!: Task;

    constructor(private kanbanBoardService: KanbanBoardService,
        private modal: NzModalService,
        private message: NzMessageService,
        private confirmationService: ConfirmationService,) {
    }

    ngOnInit(): void {
    }


    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        const { taskInfo } = changes;
        if (taskInfo) {
            if (!this.lastTaskInfo) {
                await this.getTaskConfigDetail();
            }
            this.getConfigFieldForm();
        }
        this.lastTaskInfo = this.taskInfo;
    }

    async getCategoryInfos(): Promise<{
        [categoryId: string]: boolean
    }> {
        const allCategories = await this.kanbanBoardService.getAllCategory();
        let data = {};
        allCategories.forEach(it => {
            data = {
                ...data,
                [it.id]: it.isPlural
            };
        });
        return data;
    }

    async getTaskConfigDetail(): Promise<void> {
        const { configId } = this.taskInfo;
        if (!configId) {
            return;
        }
        const data = await this.kanbanBoardService.getTaskConfigDetail(configId);
        this.templateFields = data.configFields || [];
        this.templateFieldObj = this.kanbanBoardService.translateArrayToObj(this.templateFields) as {
            [fieldName: string]: TaskConfigField
        };
        console.log('getTaskConfigDetail', this.templateFields, this.templateFieldObj);
    }

    changeTaskStatus(swimLane: TaskSwimLane): void {
        console.log('changeTaskStatus', this.inactiveUsers);
        if (swimLane.id === this.taskInfo.swimLaneId) {
            return;
        }
        this.statusLoading = true;

        const swimLaneDetail = this.swimLaneList.find(it => it.id === this.taskInfo.swimLaneId);
        if (swimLane.category === 'DONE' && swimLaneDetail?.category === 'OTHER') {
            const modalRef = this.modal.create({
                nzTitle: 'Proceed with task completion?',
                nzContent: `<ul style="padding-left: 20px; margin: 0;">
                            <li><b>OK</b>: Auto-release assignments and set today as Due Date.</li>
                            <li><b>No</b>: Leave unchanged for manual review.</li>
                            </ul>`,
                nzStyle: { top: '40vh' },
                nzFooter: [
                    {
                        label: 'No',
                        onClick: async () => {
                            // original swimlane update logic without auto-release
                            if (this.taskInfo.subTaskId) {
                                const params = {
                                    taskId: this.taskInfo.subTaskId,
                                    swimLaneId: swimLane.id,
                                };
                                await this.kanbanBoardService.updateSubTaskSwimLane(params);
                            } else {
                                const params = {
                                    taskId: this.taskInfo.taskId,
                                    swimLaneId: swimLane.id,
                                };
                                await this.kanbanBoardService.updateTaskSwimLane(params);
                            }
                            const updatedParams = {
                                swimLaneId: swimLane.id,
                                swimLaneName: swimLane.swimLaneName,
                            };
                            this.taskInfo = {
                                ...this.taskInfo,
                                ...updatedParams
                            };
                            this.message.success('Status updated successfully');
                            this.saveField.emit({
                                field: {},
                                fieldKey: 'swimLane',
                                value: updatedParams
                            });
                            modalRef.close();
                        }
                    },
                    {
                        label: 'OK',
                        type: 'primary',
                        danger: true,
                        onClick: () => {
                            this.confirmationService.confirmUpdateTask(this.inactiveUsers, async () => {
                                await this.kanbanBoardService.releaseAllResource(this.taskInfo, swimLane.id);
                                const updatedParams = {
                                    swimLaneId: swimLane.id,
                                    swimLaneName: swimLane.swimLaneName,
                                };
                                this.taskInfo = {
                                    ...this.taskInfo,
                                    ...updatedParams
                                };
                                this.message.success('Status updated successfully');
                                this.saveField.emit({
                                    field: {},
                                    fieldKey: 'swimLane',
                                    value: updatedParams
                                });
                                this.statusLoading = false;
                            });
                            modalRef.close();
                        }
                    }
                ],
                nzOnCancel: () => {
                    // Just close the modal, do nothing
                    this.statusLoading = false;
                    modalRef.close();
                }
            });
        } else {
            this.confirmationService.confirmUpdateTask(this.inactiveUsers, async () => {
                if (this.taskInfo.subTaskId) {
                    const params = {
                        taskId: this.taskInfo.subTaskId,
                        swimLaneId: swimLane.id,
                    };
                    await this.kanbanBoardService.updateSubTaskSwimLane(params);
                } else {
                    const params = {
                        taskId: this.taskInfo.taskId,
                        swimLaneId: swimLane.id,
                    };
                    await this.kanbanBoardService.updateTaskSwimLane(params);
                }
                const updatedParams = {
                    swimLaneId: swimLane.id,
                    swimLaneName: swimLane.swimLaneName,
                };
                this.taskInfo = {
                    ...this.taskInfo,
                    ...updatedParams
                };
                this.message.success('Status updated successfully');
                this.saveField.emit({
                    field: {},
                    fieldKey: 'swimLane',
                    value: updatedParams
                });
            }, () => { this.handleCancel(); });
        }
        if (this.statusLoading) this.statusLoading = false;
    }

    async handleCancel(): Promise<void> {
        if (this.taskInfo.subTaskId) {
            const params = {
                taskId: this.taskInfo.subTaskId,
                swimLaneId: this.taskInfo.swimLaneId,
            };
            await this.kanbanBoardService.updateSubTaskSwimLane(params);
        } else {
            const params = {
                taskId: this.taskInfo.taskId,
                swimLaneId: this.taskInfo.swimLaneId,
            };
            await this.kanbanBoardService.updateTaskSwimLane(params);
        }
        const currentSwimLaneId = this.taskInfo.swimLaneId;
        const currentSwimLaneName = this.taskInfo.swimLaneName;
        const updatedParams = {
            swimLaneId: currentSwimLaneId,
            swimLaneName: currentSwimLaneName,
        };
        this.taskInfo = {
            ...this.taskInfo,
            ...updatedParams
        };
        this.saveField.emit({
            field: {},
            fieldKey: 'swimLane',
            value: updatedParams
        });
    }

    getConfigFieldForm(): void {
        this.configFieldsForm = this.kanbanBoardService.translateArrayToObj(this.taskInfo.taskContentResponses) as {
            [fieldName: string]: TaskContentResponses
        };
    }

    saveFieldFn(updateItems: {
        field: TaskConfigField,
        fieldKey: string,
        value: any
    }): void {
        this.saveField.emit(updateItems);
    }

    onTaskInfoChange(updatedTaskInfo: Task): void {
        this.taskInfo = updatedTaskInfo;
        this.taskInfoChange.emit(updatedTaskInfo);
    }

    closeAllDropdowns(): void {
        this.fieldItems?.forEach(item => item.closeAllDropdowns());
    }
}
